/**
 * This store manages the loading and manipulation of scheduled events.
 * Its `events` property contains the all loaded scheduled events, while `userEvents` contains only
 * events for the current user, and any users they are covering for.
 */
import { Ref, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { unionBy, scalarArraysAreEqual } from '@/utils/array';

import {
  ScheduledEvent,
  ScheduledEventWithDates,
} from '@/types/Api/ScheduledEvent';

import {
  getEvents,
  createEvent as apiCreateEvent,
  updateEvent as apiUpdateEvent,
  deleteEvent as apiDeleteEvent,
} from '@/flagler-api/scheduledEvents';

import { getUpcomingEventsForChatId } from '@/flagler-api/chat';

import {
  dateFromDateOrString,
  dateTimeToTimeString,
  getMidnightToday,
  getOneSecondBeforeMidnightTomorrow,
} from '@/utils/date';

import { useUserStore } from '@/stores/UserStore';
import { useRtmStore } from './RtmStore';

export const useScheduledEventsStore = defineStore('ScheduledEvents', () => {
  // when we insert events, we must ensure their start and endTimes are real Dates
  const events = ref<ScheduledEventWithDates[]>([]);
  const toastedEvents: Set<string> = new Set();
  const router = useRouter();
  const { userData, allUserIds } = storeToRefs(useUserStore());
  const { users } = storeToRefs(useRtmStore());
  const todayStart = getMidnightToday();
  const todayEnd = getOneSecondBeforeMidnightTomorrow();

  const eventToEventWithUserData = (event: ScheduledEvent) => {
    if (event.user && !event.userData) {
      const userData = users.value.find((u) => u._id === event.user);
      if (userData) {
        return {
          ...event,
          userData,
        };
      }
    }
    return event;
  };

  const startup = (todaysEvents?: ScheduledEvent[]) => {
    events.value = [];
    if (todaysEvents) {
      upsertEvents(todaysEvents);
    }
  };

  const clear = () => {
    events.value = [];
    toastedEvents.clear();
    lastLoadStartTime = null;
    lastLoadEndTime = null;
  };

  const upsertEvents = (newRawEvents: ScheduledEvent[]) => {
    const newEvents: ScheduledEventWithDates[] = newRawEvents.map(
      (e: ScheduledEvent) => {
        return {
          ...eventToEventWithUserData(e),
          startTime: dateFromDateOrString(e.startTime),
          endTime: e.endTime ? dateFromDateOrString(e.endTime) : null,
        } as ScheduledEventWithDates;
      }
    );
    events.value = unionBy(
      events.value,
      newEvents,
      (e: ScheduledEventWithDates) => e._id
    );
  };

  let lastLoadStartTime: Date | null = null;
  let lastLoadEndTime: Date | null = null;
  const loadEvents = async (
    startTime: Date,
    endTime: Date,
    setLastLoadTimes = true
  ) => {
    if (setLastLoadTimes) {
      lastLoadStartTime = startTime;
      lastLoadEndTime = endTime;
    }
    const data = await getEvents(startTime, endTime, allUserIds.value);
    upsertEvents(data);
    return data;
  };

  watch(
    allUserIds,
    async (newUserIds, oldUserIds) => {
      if (
        lastLoadStartTime &&
        lastLoadEndTime &&
        !scalarArraysAreEqual(newUserIds, oldUserIds)
      ) {
        events.value = [];
        await loadEvents(lastLoadStartTime, lastLoadEndTime);
        if (
          lastLoadStartTime.getTime() !== todayStart.getTime() ||
          lastLoadEndTime.getTime() !== todayEnd.getTime()
        ) {
          await loadEvents(todayStart, todayEnd, false);
        }
      }
    },
    { deep: true }
  );

  const deleteEvent = async (eventId: string, localOnly = false) => {
    if (!localOnly) {
      await apiDeleteEvent(eventId);
    }
    events.value = events.value.filter(
      (e: ScheduledEventWithDates) => e._id !== eventId
    );
  };

  const localDeleteEvent = async (eventId: string) => {
    await deleteEvent(eventId, true);
  };

  const createEvent = async (event: ScheduledEvent & { _id?: string }) => {
    const newEvent = await apiCreateEvent(event);
    upsertEvents([newEvent]);
    return newEvent;
  };

  const updateEvent = async (event: ScheduledEvent) => {
    const updatedEvent = await apiUpdateEvent(event);
    upsertEvents([updatedEvent]);
    return updatedEvent;
  };

  const eventsForDateRange = (
    startTime: Date,
    endTime: Date,
    withEvents: ScheduledEventWithDates[]
  ): ScheduledEventWithDates[] => {
    const st = startTime.getTime();
    const et = endTime.getTime();
    return withEvents.filter((e: ScheduledEventWithDates) => {
      return e.startTime.getTime() >= st && e.startTime.getTime() <= et;
    });
  };

  const userEvents = computed(() => {
    return events.value.filter((e: ScheduledEvent) =>
      allUserIds.value.includes(e.user)
    );
  });

  return {
    // actions
    startup,
    upsertEvents,
    loadEvents,
    deleteEvent,
    localDeleteEvent,
    createEvent,
    updateEvent,
    eventsForDateRange,
    clear,

    // getters
    events,
    userEvents,
  };
});
